import { useCallback } from "react";

import { Action, Run, RunState, Stack } from "types/generated";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";

import useHasEntityWriteAccess from "./useHasEntityWriteAccess";

const CONFIRM_ACCESS_TYPES = [Action.RunConfirm];

const hasConfirmAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(CONFIRM_ACCESS_TYPES, viewer, spaceId);

const useCanConfirmRun = (initialStack?: Stack): ((run: Run, stack?: Stack) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const hasEntityWriteAccess = useHasEntityWriteAccess();

  return useCallback(
    (run: Run, stack?: Stack) => {
      const targetStack = stack || initialStack;
      if (!targetStack) {
        return { result: false };
      }

      if (run.state !== RunState.Unconfirmed) {
        return { result: false };
      }

      if (run.expired) {
        return { result: false };
      }

      if (run.needsApproval) {
        return { result: false };
      }

      if (hasEntityWriteAccess(targetStack)) {
        return { result: true };
      }

      // TODO: remove this condition for space id once we have a proper types for queries results
      if (hasConfirmAccess(viewer, targetStack.spaceDetails?.id || targetStack.space)) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [viewer, hasEntityWriteAccess, initialStack]
  );
};

export default useCanConfirmRun;
